import type { KeyboardEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Button } from '../atoms/Button/Button';
import { TAB_IDS, type TabId } from './useAquariumFormTabs';
import './AquariumFormTabs.css';

interface AquariumFormTabsProps {
  activeTab: TabId;
  onTabChange: (id: TabId) => void;
  /** Set of tab ids that have validation errors */
  errorTabs?: Set<TabId>;
}

export function AquariumFormTabs({
  activeTab,
  onTabChange,
  errorTabs = new Set(),
}: AquariumFormTabsProps) {
  const { t } = useTranslation();

  const tabLabels: Record<TabId, string> = {
    basic: t('aquarium.form.tabs.basic'),
    dimensions: t('aquarium.form.tabs.dimensions'),
    equipment: t('aquarium.form.tabs.equipment'),
    notes: t('aquarium.form.tabs.notes'),
  };

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, currentIndex: number): void {
    let nextIndex: number | null = null;

    if (e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % TAB_IDS.length;
    } else if (e.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + TAB_IDS.length) % TAB_IDS.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = TAB_IDS.length - 1;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      onTabChange(TAB_IDS[nextIndex]);
      // Move focus to the newly activated tab
      const tablist = e.currentTarget.closest('[role="tablist"]');
      if (tablist) {
        const tabs = tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        tabs[nextIndex]?.focus();
      }
    }
  }

  return (
    <div
      role="tablist"
      aria-label={t('aquarium.form.tabs.label') ?? 'Aquarium form sections'}
      className="rg-aquarium-form__tablist"
    >
      {TAB_IDS.map((id, index) => {
        const isActive = id === activeTab;
        const hasError = errorTabs.has(id);
        const classNames = [
          'rg-aquarium-form__tab',
          isActive ? 'rg-aquarium-form__tab--active' : '',
          hasError ? 'rg-aquarium-form__tab--error' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <Button
            key={id}
            variant="ghost"
            role="tab"
            id={`aq-tab-${id}`}
            aria-selected={isActive}
            aria-controls={`aq-tabpanel-${id}`}
            // Error state is announced as text, not only shown as a colour dot.
            aria-label={hasError ? t('aquarium.form.tabs.hasError', { tab: tabLabels[id] }) : undefined}
            tabIndex={isActive ? 0 : -1}
            className={classNames}
            onClick={() => { onTabChange(id); }}
            onKeyDown={(e) => { handleKeyDown(e, index); }}
          >
            {tabLabels[id]}
            {hasError && (
              <span className="rg-aquarium-form__tab-error-icon" aria-hidden="true">!</span>
            )}
          </Button>
        );
      })}
    </div>
  );
}
